import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import toast from "react-hot-toast";
import { getUserData } from "../pages/const";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import {
  assignKarigarToCustomOrderItem,
  assignKarigarToRepairingOrderItem,
  isErpApiSuccess,
  listBankAccounts,
  listProductGroups,
  listSuppliers,
} from "../erpClient";
import { extractListRowsFromResponse } from "../utils/parseApiPagination";

function AssignKarigarModal({ show, onHide, orderData, onAssignSuccess }) {
  const [karigars, setKarigars] = useState([]);
  const [selectedKarigarId, setSelectedKarigarId] = useState("");
  const [loading, setLoading] = useState(false);
  const [assignmentDate, setAssignmentDate] = useState(() => {
    const now = new Date();
    return now.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:MM
  });

  // Product groups and assignments
  const [productGroups, setProductGroups] = useState([]);
  const [itemAssignments, setItemAssignments] = useState({});
  const [productGroupNames, setProductGroupNames] = useState({});
  const [assignSameKarigar, setAssignSameKarigar] = useState(false);
  const [sameKarigarId, setSameKarigarId] = useState("");

  // Bank accounts and payment modes
  const [bankAccounts, setBankAccounts] = useState([]);
  const [goldPaymentMode, setGoldPaymentMode] = useState("Gold");
  const [goldBankAccount, setGoldBankAccount] = useState(null);
  const [silverPaymentMode, setSilverPaymentMode] = useState("Silver");
  const [silverBankAccount, setSilverBankAccount] = useState(null);

  // Payment mode options for React Select
  const PAYMENT_MODE_OPTIONS = [
    { value: "Cash", label: "Cash" },
    { value: "UPI", label: "UPI" },
    { value: "Bank Transfer", label: "Bank Transfer" },
    { value: "Cheque", label: "Cheque" },
    { value: "Card", label: "Card" },
  ];

  // Fetch product groups and bank accounts on component mount
  useEffect(() => {
    if (show) {
      // Don't fetch all karigars upfront, use search instead
      fetchProductGroups();
      fetchBankAccounts();
    }
  }, [show]);

  // Listen for API URL changes and close modal
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in AssignKarigarModal, closing modal...', event.detail);
      if (show) {
        toast('Database switched. Closing modal.');
        onHide();
      }
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [show, onHide]);

  // Debug effect to log bank account states
  useEffect(() => {
    // console.log("Gold bank account state:", goldBankAccount);
    // console.log("Silver bank account state:", silverBankAccount);
    // console.log("All bank accounts:", bankAccounts);
  }, [goldBankAccount, silverBankAccount, bankAccounts]);

  const fetchKarigarsByPrefix = async (prefix) => {
    try {
      if (!prefix || prefix.length < 3) {
        setKarigars([]);
        return;
      }

      const response = await listSuppliers({ search: prefix });
      let list = [];
      if (isErpApiSuccess(response)) {
        list = extractListRowsFromResponse(response, ["suppliers"]);
      }

      if (list.length > 0) {
        // Filter active karigars
        const activeKarigars = list.filter(
          (karigar) => karigar.status === 1 || karigar.status === "1"
        );

        const fetchedKarigars = activeKarigars.map((karigar) => ({
          id: karigar.id,
          fullName: karigar.name || "",
          companyName: karigar.company_name || "",
          shortCode: karigar.short_code || "",
          contactNumber: karigar.contact_number || "",
          email: karigar.email || "",
          address: karigar.address || "",
          city: karigar.city || "",
          gstNumber: karigar.gst_number || karigar.GST_number || "",
          status: karigar.status || "",
          isPrimary: karigar.primary_supplier === 1 || karigar.primary_karigar === "1" ? 1 : 0,
          gold: karigar.gold || "0",
          silver: karigar.silver || "0",
          balance: karigar.balance || "0",
        }));

        // Sort by ID in descending order (recent first)
        fetchedKarigars.sort((a, b) => Number(b.id) - Number(a.id));
        setKarigars(fetchedKarigars);
      } else {
        setKarigars([]);
      }
    } catch (error) {
      console.error("Error fetching karigars:", error);
      toast.error("Failed to fetch karigars");
      setKarigars([]);
    }
  };

  // Function for AsyncSelect to load karigar options
  const loadKarigarOptions = async (inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) {
      callback([]);
      return;
    }

    try {
      await fetchKarigarsByPrefix(inputValue);
      // Transform karigars to select options
      const options = karigars.map((karigar) => ({
        value: karigar.id,
        label: `${karigar.fullName}${
          karigar.companyName ? ` (${karigar.companyName})` : ""
        }${karigar.shortCode ? ` - ${karigar.shortCode}` : ""}${
          karigar.isPrimary === 1 ? " - Primary" : ""
        }`,
      }));
      callback(options);
    } catch (error) {
      console.error("Error loading karigar options:", error);
      callback([]);
    }
  };

  // Alternative approach: Return a promise for AsyncSelect
  const loadKarigarOptionsPromise = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      return [];
    }

    try {
      const response = await listSuppliers({ search: inputValue });
      let list = [];
      if (isErpApiSuccess(response)) {
        list = extractListRowsFromResponse(response, ["suppliers"]);
      }

      const activeKarigars = list.filter(
        (karigar) => karigar.status === 1 || karigar.status === "1",
      );

      return activeKarigars.map((karigar) => ({
        value: karigar.id,
        label: `${karigar.name || ""}${
          karigar.company_name ? ` (${karigar.company_name})` : ""
        }${karigar.short_code ? ` - ${karigar.short_code}` : ""}${
          karigar.primary_supplier === 1 || karigar.primary_karigar === "1"
            ? " - Primary"
            : ""
        }`,
      }));
    } catch (error) {
      console.error("Error loading karigar options:", error);
      return [];
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await listBankAccounts();

      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        const fetchedBankDetails = response.data.bank_accounts.map((bank) => ({
          id: bank.id,
          bankName: bank.bank_name || "",
          accountHolder: bank.holder_name || "",
          accountType: bank.account_type || "",
          ifscCode: bank.ifsc_code || bank.IFSC_code || "",
          accountNumber: bank.account_number || "",
          openingBalance: bank.opening_balance || "",
          balance:
            bank.available_balance !== null &&
              bank.available_balance !== undefined
              ? bank.account_type === "metal"
                ? `${bank.available_balance} gm`
                : `₹${bank.available_balance}`
              : bank.account_type === "metal"
                ? "0 gm"
                : "₹0",
          status: bank.status === 1 ? "Active" : "Inactive",
          statusClass:
            bank.status === 1
              ? "bg-success-subtle text-success"
              : "bg-danger-subtle text-danger",
          useForInvoice: bank.invoice === 1,
          useForPOS: bank.pos === 1,
        }));

        fetchedBankDetails.sort((a, b) => Number(b.id) - Number(a.id));
        setBankAccounts(fetchedBankDetails);
      } else if (response?.success && !response.data?.bank_accounts?.length) {
        setBankAccounts([]);
        toast("No bank accounts found");
      } else if (response && response.success === false) {
        setBankAccounts([]);
        toast.error(response.message || "Failed to fetch bank details");
      } else {
        setBankAccounts([]);
        toast.error("Unexpected response format from server");
      }
    } catch (error) {
      setBankAccounts([]);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch bank details"
      );
    }
  };

  const handleAssignKarigar = async () => {
    // Check if at least one item has a karigar assigned
    const hasAnyAssignment = Object.values(itemAssignments).some(
      (assignment) => assignment.karigar_id
    );
    if (!hasAnyAssignment) {
      toast.error("Please assign at least one karigar to an item");
      return;
    }

    if (!assignmentDate) {
      toast.error("Please select assignment date");
      return;
    }

    setLoading(true);
    try {
      // Process each item assignment
      const assignmentPromises = Object.entries(itemAssignments).map(
        async ([itemId, assignment]) => {
          if (!assignment.karigar_id) return null; // Skip items without karigar assigned

          // Find the selected bank accounts for this assignment
          // console.log("Looking for bank with paid_from:", assignment.paid_from, typeof assignment.paid_from);
          // console.log("Available bank accounts:", bankAccounts.map(b => ({ id: b.id, name: b.bankName })));

          const selectedAmountBank = bankAccounts.find(
            (bank) => Number(bank.id) === Number(assignment.paid_from)
          );
          const selectedGoldBank = bankAccounts.find(
            (bank) => Number(bank.id) === Number(assignment.gold_bank_account)
          );
          const selectedSilverBank = bankAccounts.find(
            (bank) => Number(bank.id) === Number(assignment.silver_bank_account)
          );
          const userData = getUserData();
          // console.log("Current logged-in user data:", userData);
          // console.log("User ID from getUserData():", userData?.id);

          const assignmentData = {
            // Add type field similar to other API calls
            id: parseInt(itemId), // Custom order entry item ID
            karigar_id: parseInt(assignment.karigar_id),
            given_emp_id: userData?.id, // Current user ID from getUserData
            given_date: new Date().toISOString().slice(0, 19).replace("T", " "), // Add given_date in YYYY-MM-DD HH:MM:SS format
            gold_given: parseFloat(assignment.gold_amount || 0),
            silver_given: parseFloat(assignment.silver_amount || 0),
            amount_given: parseFloat(assignment.amount_value || 0),
            // by_user: userData?.id || 0, // Add by_user field
            // Send payment mode based on what's being given
            ...(parseFloat(assignment.gold_amount || 0) > 0 &&
              goldPaymentMode && { gold_pay_mode: goldPaymentMode }),
            ...(parseFloat(assignment.silver_amount || 0) > 0 &&
              silverPaymentMode && { silver_pay_mode: silverPaymentMode }),
            ...(parseFloat(assignment.amount_value || 0) > 0 &&
              assignment.payment_mode && {
                amount_pay_mode: assignment.payment_mode,
              }),
            // Send bank type based on what's being given and from which account
            ...(parseFloat(assignment.gold_amount || 0) > 0 &&
              selectedGoldBank && { gold_bank_type: selectedGoldBank.id }),
            ...(parseFloat(assignment.silver_amount || 0) > 0 &&
              selectedSilverBank && {
                silver_bank_type: selectedSilverBank.id,
              }),
            ...(parseFloat(assignment.amount_value || 0) > 0 &&
              selectedAmountBank && {
                amount_bank_type: selectedAmountBank.id,
              }),
          };

          // console.log(
          //   `Sending assignment data for item ${itemId}:`,
          //   assignmentData
          // );
          // console.log("Paid From value:", assignment.paid_from, typeof assignment.paid_from);
          // console.log("Selected Amount Bank:", selectedAmountBank);
          // console.log("Full assignment object:", assignment);

          const requestData = orderData?.isRepairingOrder
            ? {
                ...assignmentData,
                // Always include by_user field
                // by_user: assignmentData.by_user,
                // Only include fields that have values, don't send empty strings
                ...(assignmentData.gold_bank_type &&
                  assignmentData.gold_bank_type !== "" && {
                    gold_bank_type: assignmentData.gold_bank_type,
                  }),
                ...(assignmentData.silver_bank_type &&
                  assignmentData.silver_bank_type !== "" && {
                    silver_bank_type: assignmentData.silver_bank_type,
                  }),
                ...(assignmentData.amount_bank_type &&
                  assignmentData.amount_bank_type !== "" && {
                    amount_bank_type: assignmentData.amount_bank_type,
                  }),
                ...(assignmentData.gold_pay_mode &&
                  assignmentData.gold_pay_mode !== "" && {
                    gold_pay_mode: assignmentData.gold_pay_mode,
                  }),
                ...(assignmentData.silver_pay_mode &&
                  assignmentData.silver_pay_mode !== "" && {
                    silver_pay_mode: assignmentData.silver_pay_mode,
                  }),
                ...(assignmentData.amount_pay_mode &&
                  assignmentData.amount_pay_mode !== "" && {
                    amount_pay_mode: assignmentData.amount_pay_mode,
                  }),
              }
            : {
                ...assignmentData,
                // Always include by_user field for custom orders too
                // by_user: assignmentData.by_user,
              };

          const idempotency_key =
            typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

          const body = orderData?.isRepairingOrder
            ? await assignKarigarToRepairingOrderItem({
                ...requestData,
                idempotency_key,
              })
            : await assignKarigarToCustomOrderItem({
                ...requestData,
                idempotency_key,
              });

          return body;
        }
      );

      // Filter out null promises and execute all assignments
      const validPromises = assignmentPromises.filter(
        (promise) => promise !== null
      );
      const responses = await Promise.all(validPromises);

      // console.log(
      //   "All API responses:",
      //   responses.map((r) => r.data)
      // );

      // Check if all assignments were successful
      const successfulResponses = [];
      const failedResponses = [];

      responses.forEach((body) => {
        const ok =
          isErpApiSuccess(body) && body?.success !== false && !body?.error;
        if (ok) {
          successfulResponses.push(body);
        } else {
          failedResponses.push(body);
        }
      });

      if (failedResponses.length === 0) {
        // All successful
        const successMessage = "Karigar assigned successfully!";
        toast.success(successMessage);
        onAssignSuccess && onAssignSuccess();
      } else if (successfulResponses.length === 0) {
        // All failed
        const errorMessage =
          failedResponses[0]?.message ||
          "All assignments failed. Please try again.";
        toast.error(errorMessage);
      } else {
        // Mixed results
        toast.error(
          `${failedResponses.length} assignment(s) failed. ${successfulResponses.length} succeeded.`
        );
      }
    } catch (error) {
      console.error("Error assigning karigar:", error);
      console.error("Error response data:", error.response?.data);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to assign karigar";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductGroups = async () => {
    try {
      // Extract product groups from orderData.custom_order_entry
      if (
        orderData &&
        orderData.custom_order_entry &&
        Array.isArray(orderData.custom_order_entry)
      ) {
        const groups = orderData.custom_order_entry.map((item) => ({
          id: item.id,
          order_number: item.order_number,
          product_group: item.product_group,
          design: item.design,
          size: item.size,
          pcs: item.pcs,
          gwt: item.gwt,
          lwt: item.lwt,
          nwt: item.nwt,
          totalamt: item.totalamt,
          status: item.status,
        }));
        // console.log("Product Groups for Modal:", groups);
        setProductGroups(groups);

        // Initialize assignments for each item
        const initialAssignments = {};
        groups.forEach((item) => {
          initialAssignments[item.id] = {
            karigar_id: "",
            gold_amount: "",
            silver_amount: "",
            amount_value: "",
            note: "",
            payment_mode: "",
            paid_from: "",
            gold_bank_account: "",
            silver_bank_account: "",
          };
        });
        setItemAssignments(initialAssignments);

        // Fetch product group names
        const response = await listProductGroups();
        if (response?.success && Array.isArray(response.data?.product_groups)) {
          const names = {};
          response.data.product_groups.forEach((group) => {
            names[group.id] = group.name || "";
          });
          setProductGroupNames(names);
        }
      }
    } catch (error) {
      console.error("Error processing product groups:", error);
      toast.error("Failed to load product groups");
    }
  };

  const handleItemKarigarSelect = (itemId, karigarId, karigarName = "") => {
    if (assignSameKarigar) {
      // When checkbox is checked, update all items with the same karigar
      const updatedAssignments = {};
      productGroups.forEach((item) => {
        updatedAssignments[item.id] = {
          ...itemAssignments[item.id],
          karigar_id: karigarId,
          karigar_name: karigarName,
        };
      });
      setItemAssignments(updatedAssignments);
    } else {
      // When checkbox is unchecked, update only the selected item
      setItemAssignments((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          karigar_id: karigarId,
          karigar_name: karigarName,
        },
      }));
    }
  };

  const handleItemFieldChange = (itemId, field, value) => {
    // console.log(`handleItemFieldChange: itemId=${itemId}, field=${field}, value=`, value, typeof value);

    setItemAssignments((prev) => {
      const updatedAssignment = {
        ...prev[itemId],
        [field]: value,
      };

      // Auto-select gold bank when gold amount is entered
      if (field === "gold_amount" && value && parseFloat(value) > 0) {
        const goldBank = bankAccounts.find((bank) =>
          bank.bankName.toLowerCase().includes("gold")
        );
        if (goldBank) {
          updatedAssignment.gold_bank_account = goldBank.id;
        }
      }

      // Auto-select silver bank when silver amount is entered
      if (field === "silver_amount" && value && parseFloat(value) > 0) {
        const silverBank = bankAccounts.find((bank) =>
          bank.bankName.toLowerCase().includes("silver")
        );
        if (silverBank) {
          updatedAssignment.silver_bank_account = silverBank.id;
        }
      }

      // Clear gold bank account when gold amount is cleared
      if (field === "gold_amount" && (!value || parseFloat(value) === 0)) {
        updatedAssignment.gold_bank_account = "";
      }

      // Clear silver bank account when silver amount is cleared
      if (field === "silver_amount" && (!value || parseFloat(value) === 0)) {
        updatedAssignment.silver_bank_account = "";
      }

      return {
        ...prev,
        [itemId]: updatedAssignment,
      };
    });
  };

  const handleSameKarigarChange = (karigarId) => {
    setSameKarigarId(karigarId);
    if (assignSameKarigar && karigarId) {
      // Assign the same karigar to all items
      const updatedAssignments = {};
      productGroups.forEach((item) => {
        updatedAssignments[item.id] = {
          ...itemAssignments[item.id],
          karigar_id: karigarId,
        };
      });
      setItemAssignments(updatedAssignments);
    }
  };

  const handleAssignSameKarigarToggle = (checked) => {
    setAssignSameKarigar(checked);
    if (!checked) {
      // Clear all karigar assignments when unchecking
      const updatedAssignments = {};
      productGroups.forEach((item) => {
        updatedAssignments[item.id] = {
          ...itemAssignments[item.id],
          karigar_id: "",
        };
      });
      setItemAssignments(updatedAssignments);
    }
  };

  // Get all selected karigar data for displaying badges in header
  const getAllSelectedKarigarData = () => {
    if (assignSameKarigar && sameKarigarId) {
      // When same karigar is assigned to all items, return just that one karigar
      const karigar = karigars.find(
        (k) => k.id.toString() === sameKarigarId.toString()
      );
      return karigar ? [karigar] : [];
    }

    // Get all unique assigned karigars from different items
    const assignedKarigarIds = Object.values(itemAssignments)
      .map((assignment) => assignment.karigar_id)
      .filter((id) => id) // Remove empty/null values
      .filter((id, index, array) => array.indexOf(id) === index); // Remove duplicates

    return assignedKarigarIds
      .map((id) => karigars.find((k) => k.id.toString() === id.toString()))
      .filter(Boolean); // Remove any undefined results
  };

  const selectedKarigars = getAllSelectedKarigarData();

  // Ensures keyboard focus highlight is always visible (overrides Bootstrap CSS resets)
  const reactSelectStyles = {
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#2684ff"
        : state.isFocused
        ? "#deebff"
        : "transparent",
      color: state.isSelected ? "#fff" : "#111",
      cursor: "pointer",
      padding: "8px 12px",
    }),
    control: (base, state) => ({
      ...base,
      boxShadow: state.isFocused ? "0 0 0 2px rgba(38,132,255,0.25)" : base.boxShadow,
      borderColor: state.isFocused ? "#2684ff" : base.borderColor,
    }),
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <div className="d-flex w-100 flex-row align-items-center justify-content-between gap-2">
          <Modal.Title className="mb-0">Assign Karigar</Modal.Title>
          {selectedKarigars.length > 0 && (
            <div className="d-flex flex-row flex-wrap gap-3 align-items-center justify-content-end w-auto">
              {selectedKarigars.map((karigar, index) => (
                <div
                  key={karigar.id}
                  className="d-flex flex-row flex-wrap gap-1 align-items-center"
                >
                  {selectedKarigars.length > 1 && (
                    <small
                      className="text-muted me-1"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {karigar.fullName}:
                    </small>
                  )}
                  <span
                    className={`badge ${
                      parseFloat(karigar.gold) > 0
                        ? "bg-success-subtle text-success"
                        : parseFloat(karigar.gold) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                    }`}
                    style={{ fontSize: "0.85rem" }}
                  >
                    Gold: {parseFloat(karigar.gold || 0).toFixed(2)} gm
                  </span>
                  <span
                    className={`badge ${
                      parseFloat(karigar.silver) > 0
                        ? "bg-success-subtle text-success"
                        : parseFloat(karigar.silver) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                    }`}
                    style={{ fontSize: "0.85rem" }}
                  >
                    Silver: {parseFloat(karigar.silver || 0).toFixed(2)} gm
                  </span>
                  <span
                    className={`badge ${
                      parseFloat(karigar.balance) > 0
                        ? "bg-success-subtle text-success"
                        : parseFloat(karigar.balance) < 0
                        ? "bg-danger-subtle text-danger"
                        : "bg-light text-dark"
                    }`}
                    style={{ fontSize: "0.85rem" }}
                  >
                    Balance: ₹{parseFloat(karigar.balance || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Assign Same Karigar Checkbox - Only show if more than 1 item */}
          {productGroups.length > 1 && (
            <Row className="mb-3">
              <Col md={8}></Col>
              <Col md={4} className="d-flex justify-content-end">
                <Form.Check
                  type="checkbox"
                  id="assignSameKarigar"
                  label="Same Karigar for all items"
                  checked={assignSameKarigar}
                  onChange={(e) =>
                    handleAssignSameKarigarToggle(e.target.checked)
                  }
                />
              </Col>
            </Row>
          )}

          {/* Product Groups and Item Assignments */}
          {productGroups.length > 0 && (
            <div className="mb-4">
              {productGroups.map((item, index) => (
                <div key={item.id} className="mb-3">
                  <Form.Group className="mb-3">
                    <Row>
                      <Col md={3}>
                        <Form.Label>Item {index + 1}</Form.Label>
                        <Form.Control
                          type="text"
                          value={
                            productGroupNames[item.product_group] ||
                            item.product_group_name ||
                            item.product_group ||
                            "No Product Group"
                          }
                          readOnly
                          className="bg-light"
                        />
                        {(item.gwt || item.nwt) && (
                          <div
                            className="mt-1"
                            style={{ marginLeft: "0.3rem" }}
                          >
                            <small className="badge bg-info-subtle text-info">
                              GWT: {parseFloat(item.gwt || 0).toFixed(2)} gm
                              {item.nwt && (
                                <>
                                  {" "}
                                  | NWT: {parseFloat(item.nwt || 0).toFixed(
                                    2
                                  )}{" "}
                                  gm
                                </>
                              )}
                            </small>
                          </div>
                        )}
                      </Col>

                      <Col md={3}>
                        <Form.Label>Karigar</Form.Label>
                        <AsyncSelect
                          loadOptions={loadKarigarOptionsPromise}
                          value={
                            itemAssignments[item.id]?.karigar_id
                              ? {
                                  value: itemAssignments[item.id]?.karigar_id,
                                  label:
                                    itemAssignments[item.id]?.karigar_name ||
                                    "Unknown Karigar",
                                }
                              : null
                          }
                          onChange={(selectedOption) =>
                            handleItemKarigarSelect(
                              item.id,
                              selectedOption ? selectedOption.value : "",
                              selectedOption ? selectedOption.label : ""
                            )
                          }
                          isClearable
                          isSearchable
                          classNamePrefix="karigar-select"
                          styles={reactSelectStyles}
                          noOptionsMessage={({ inputValue }) =>
                            inputValue && inputValue.length < 3
                              ? "Type at least 3 characters"
                              : "No karigars found"
                          }
                        />
                      </Col>

                      <Col md={6}>
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          placeholder="Enter any additional notes for this item..."
                          value={itemAssignments[item.id]?.note || ""}
                          onChange={(e) =>
                            handleItemFieldChange(
                              item.id,
                              "note",
                              e.target.value
                            )
                          }
                        />
                      </Col>
                    </Row>

                    <Row className="mt-2">
                      <Col md={2}>
                        <Form.Label>Gold</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={itemAssignments[item.id]?.gold_amount || ""}
                          onChange={(e) =>
                            handleItemFieldChange(
                              item.id,
                              "gold_amount",
                              e.target.value
                            )
                          }
                        />
                      </Col>
                      <Col md={2}>
                        <Form.Label>Silver</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={itemAssignments[item.id]?.silver_amount || ""}
                          onChange={(e) =>
                            handleItemFieldChange(
                              item.id,
                              "silver_amount",
                              e.target.value
                            )
                          }
                        />
                      </Col>
                      <Col md={2}>
                        <Form.Label>Amount</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={itemAssignments[item.id]?.amount_value || ""}
                          onChange={(e) =>
                            handleItemFieldChange(
                              item.id,
                              "amount_value",
                              e.target.value
                            )
                          }
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Label>Payment Mode</Form.Label>
                        <Select
                          options={PAYMENT_MODE_OPTIONS}
                          value={
                            PAYMENT_MODE_OPTIONS.find(
                              (option) =>
                                option.value ===
                                itemAssignments[item.id]?.payment_mode
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            handleItemFieldChange(
                              item.id,
                              "payment_mode",
                              selectedOption ? selectedOption.value : ""
                            )
                          }
                          isClearable
                          isSearchable
                          classNamePrefix="karigar-select"
                          styles={reactSelectStyles}
                          placeholder="Select Mode"
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Label>Paid From</Form.Label>
                        <Select
                          options={bankAccounts.map((bank) => ({
                            value: Number(bank.id),
                            label: bank.bankName,
                          }))}
                          value={(() => {
                            const currentValue =
                              itemAssignments[item.id]?.paid_from;
                            const options = bankAccounts.map((bank) => ({
                              value: Number(bank.id),
                              label: bank.bankName,
                            }));
                            const selected = options.find(
                              (option) =>
                                Number(option.value) === Number(currentValue)
                            );
                            return selected || null;
                          })()}
                          onChange={(selectedOption) =>
                            handleItemFieldChange(
                              item.id,
                              "paid_from",
                              selectedOption ? Number(selectedOption.value) : ""
                            )
                          }
                          isClearable
                          isSearchable
                          classNamePrefix="karigar-select"
                          styles={reactSelectStyles}
                          placeholder="Select Bank"
                        />
                      </Col>
                    </Row>

                    {/* Gold and Silver Bank Accounts are auto-selected based on bank names */}
                    {/* Gold Bank Account Selection - Hidden since it's automatic */}
                    {/* Silver Bank Account Selection - Hidden since it's automatic */}
                  </Form.Group>
                </div>
              ))}
            </div>
          )}

          {/* Note Field */}
          {/* <Form.Group className="mb-3">
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter any additional notes..."
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
            />
          </Form.Group> */}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleAssignKarigar}
          disabled={!assignmentDate || loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Assigning...
            </>
          ) : (
            <>Assign</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AssignKarigarModal;
